import { useState } from "react";

/** More than this and you are stocking a shop, not a kitchen. */
export const MAX_PACKS = 24;

/**
 * How many of a thing arrived.
 *
 * Counts PACKAGES — a different question from `QuantityField`, which asks how
 * much is in one. Two 1 L cartons are count 2, size 1 L.
 *
 * Same stepper, same rule about typing: the box holds a text draft so it can be
 * emptied on the way to a new number, and the last good count comes back if it
 * is left blank.
 */
export function CountField({
  value,
  onChange,
  idPrefix = "count",
}: {
  value: number;
  onChange: (next: number) => void;
  idPrefix?: string;
}) {
  const [draft, setDraft] = useState(String(value));
  // Synced during render rather than in an effect — see QuantityField.
  const [seen, setSeen] = useState(value);
  if (value !== seen) {
    setSeen(value);
    setDraft(String(value));
  }

  const clamp = (n: number) => Math.min(MAX_PACKS, Math.max(1, Math.round(n)));

  return (
    <div className="stepper" data-testid={`${idPrefix}-stepper`}>
      <button
        type="button"
        className="step"
        onClick={() => onChange(clamp(value - 1))}
        disabled={value <= 1}
        aria-label="One fewer"
        data-testid={`${idPrefix}-minus`}
      >
        −
      </button>
      <input
        className="step-amount"
        type="number"
        inputMode="numeric"
        min="1"
        max={MAX_PACKS}
        step="1"
        value={draft}
        aria-label="How many"
        onChange={(e) => {
          setDraft(e.target.value);
          const n = Number(e.target.value);
          if (e.target.value !== "" && Number.isFinite(n) && n >= 1) onChange(clamp(n));
        }}
        onBlur={() => setDraft(String(value))}
        data-testid={`${idPrefix}-amount`}
      />
      <button
        type="button"
        className="step"
        onClick={() => onChange(clamp(value + 1))}
        disabled={value >= MAX_PACKS}
        aria-label="One more"
        data-testid={`${idPrefix}-plus`}
      >
        +
      </button>
    </div>
  );
}
